import { useState, useRef, useEffect } from "react";
import { BookingLanguage } from "@/types/booking";
import { ChevronDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { safeUpdateHistory } from "@/utils/urlUtils";
import { useMarket } from "@/hooks/useMarket";
import { getLanguagesForMarket } from "@/config/market";

interface LanguageSelectorProps {
  language: BookingLanguage;
  setLanguage: (lang: BookingLanguage) => void;
  skeletonMode?: boolean;
}

const options: { value: BookingLanguage; label: string }[] = [
  { value: "en-US", label: "EN" },
  { value: "de-DE", label: "DE" },
  { value: "fr-FR", label: "FR" },
  { value: "nl-NL", label: "NL" },
];

const LanguageSelector = ({
  language,
  setLanguage,
  skeletonMode = false,
}: LanguageSelectorProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { market } = useMarket();
  // French is Switzerland-only — the DE market lists EN/DE only.
  const marketLanguages = getLanguagesForMarket(market);
  const visibleOptions = options.filter((o) =>
    marketLanguages.includes(o.value),
  );

  const handleLanguageChange = (value: BookingLanguage) => {
    setOpen(false);
    setLanguage(value);

    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set(
      "locale",
      value === "de-DE"
        ? "de"
        : value === "fr-FR"
          ? "fr"
          : value === "nl-NL"
            ? "nl"
            : "en",
    );

    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    safeUpdateHistory("replaceState", { locale: value }, newUrl);

    console.log(`Language changed to ${value}, URL updated to ${newUrl}`);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  const selectedLabel = options.find((o) => o.value === language)?.label;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        data-testid="language-selector"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "inline-flex items-center gap-1 rounded-full border px-3 py-1",
          "text-sm ring-offset-background",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "[&>span]:line-clamp-1",
          "h-8 md:h-10 bg-transparent border-transparent shadow-none hover:border-gray-200",
          "font-sans normal-case",
        )}
      >
        <div className="flex flex-col text-left">
          <div className="flex items-center gap-2">
            <span style={{ pointerEvents: "none" }}>
              {skeletonMode ? "\u00A0" : selectedLabel}
            </span>
          </div>
        </div>
        <ChevronDown className="h-4 w-4 opacity-50" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="listbox"
          className={cn(
            "absolute right-0 top-full mt-1 z-50 min-w-[8rem]",
            "overflow-hidden rounded-md border bg-popover p-1",
            "text-popover-foreground shadow-md",
            "animate-in fade-in-0 zoom-in-95 slide-in-from-top-2",
            "font-sans",
          )}
        >
          {visibleOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={language === option.value}
              data-testid={`language-option-${option.value}`}
              onClick={() => handleLanguageChange(option.value)}
              className={cn(
                "relative flex w-full cursor-default select-none items-center",
                "rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none",
                "hover:bg-accent hover:text-accent-foreground",
                "normal-case font-sans",
              )}
            >
              <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                {language === option.value && <Check className="h-4 w-4" />}
              </span>
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
